import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler,
} from 'chart.js';
import { ThemeContext } from '../../context/ThemeContext';
import apiClient from '../../services/apiClient';
import './FbilBenchmarksRedesign.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  ChartTooltip,
  ChartLegend,
  Filler
);

/** Same catalog as fbil_benchmark_rates_scraper.py BENCHMARKS */
export const FBIL_BENCHMARKS = [
  { key: 'ovnmibor', label: 'Overnight MIBOR', hint: 'Call money overnight' },
  { key: 'cd', label: 'CD', hint: 'Certificate of Deposit' },
  { key: 'fwdpremia', label: 'Fwd Premia', hint: 'Forward Premia Curve' },
  { key: 'miborois', label: 'MIBOR-OIS', hint: 'MIBOR OIS curve' },
  { key: 'mifor', label: 'MIFOR', hint: 'Mumbai Interbank Forward' },
  { key: 'tbill', label: 'T-Bill', hint: 'Treasury Bill rates' },
  { key: 'termmibor', label: 'Term MIBOR', hint: 'Term MIBOR tenors' },
  { key: 'mror', label: 'MROR', hint: 'Market Repo' },
  { key: 'sorr', label: 'SORR', hint: 'Secured Overnight' },
  { key: 'fcrupee', label: 'FC Rupee', hint: 'Options volatility' },
  { key: 'refrates', label: 'Ref Rates', hint: 'Reference rates' },
  { key: 'modifiedmifor', label: 'Mod MIFOR', hint: 'Modified MIFOR' },
];

const SERIES_COLORS = [
  '#3B82F6',
  '#14B8A6',
  '#F59E0B',
  '#8B5CF6',
  '#EF4444',
  '#0EA5E9',
  '#84CC16',
  '#EC4899',
  '#6366F1',
  '#F97316',
  '#06B6D4',
  '#22C55E',
];

const MAX_CHART_POINTS = 96;
const CAROUSEL_AUTOPLAY_MS = 7000;

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 48 : -48,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? -48 : 48,
    opacity: 0,
  }),
};

const hexToRgba = (hex, alpha) => {
  const raw = hex.replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const areaGradient = (ctx, chartArea, color) => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, hexToRgba(color, 0.38));
  gradient.addColorStop(0.45, hexToRgba(color, 0.14));
  gradient.addColorStop(1, hexToRgba(color, 0.02));
  return gradient;
};

const formatProductLabel = (name) => {
  if (!name) return '—';
  return String(name)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDateLabel = (iso, prevIso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    const month = d.toLocaleDateString('en-GB', { month: 'short' });
    const day = d.getDate();
    const year = String(d.getFullYear()).slice(-2);
    if (!prevIso) {
      return `${month} '${year}`;
    }
    const prev = new Date(`${prevIso}T00:00:00`);
    if (prev.getMonth() !== d.getMonth() || prev.getFullYear() !== d.getFullYear()) {
      return `${month} '${year}`;
    }
    return `${day} ${month}`;
  } catch {
    return iso;
  }
};

const sampleDates = (datesAsc, maxPoints = MAX_CHART_POINTS) => {
  if (datesAsc.length <= maxPoints) return datesAsc;
  const out = [];
  const last = datesAsc.length - 1;
  for (let i = 0; i < maxPoints; i += 1) {
    const idx = Math.round((i * last) / (maxPoints - 1));
    out.push(datesAsc[idx]);
  }
  return [...new Set(out)];
};

const buildChartBundle = (products, isDark) => {
  if (!products.length) {
    return { chartData: null, options: null };
  }

  const dateSet = new Set();
  products.forEach((p) => {
    (p.benchMarkTrend || []).forEach((pt) => {
      if (pt?.publishDate) dateSet.add(pt.publishDate);
    });
  });
  const labelsAsc = sampleDates(Array.from(dateSet).sort());

  let yMin = Number.POSITIVE_INFINITY;
  let yMax = Number.NEGATIVE_INFINITY;

  const datasets = products.map((p, idx) => {
    const byDate = new Map(
      (p.benchMarkTrend || [])
        .filter((pt) => pt?.publishDate != null && pt?.rate != null)
        .map((pt) => [pt.publishDate, Number(pt.rate)])
    );
    const data = labelsAsc.map((d) => {
      if (!byDate.has(d)) return null;
      const v = byDate.get(d);
      if (Number.isFinite(v)) {
        yMin = Math.min(yMin, v);
        yMax = Math.max(yMax, v);
      }
      return v;
    });

    const color = SERIES_COLORS[idx % SERIES_COLORS.length];
    return {
      label: formatProductLabel(p.productName),
      data,
      borderColor: color,
      backgroundColor: (context) => {
        const { chart } = context;
        const { ctx, chartArea } = chart;
        if (!chartArea) return hexToRgba(color, 0.15);
        return areaGradient(ctx, chartArea, color);
      },
      borderWidth: 3.5,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHoverBorderWidth: 2,
      pointHoverBorderColor: '#ffffff',
      pointHoverBackgroundColor: color,
      tension: 0.35,
      spanGaps: true,
      fill: true,
      cubicInterpolationMode: 'monotone',
    };
  });

  const pad = Number.isFinite(yMin) && Number.isFinite(yMax)
    ? Math.max((yMax - yMin) * 0.18, 0.08)
    : 0.15;

  const chartData = {
    labels: labelsAsc.map((d, i) => formatDateLabel(d, i > 0 ? labelsAsc[i - 1] : null)),
    datasets,
  };

  const tickColor = isDark ? '#9a9b96' : '#6b7280';
  const gridY = isDark ? 'rgba(244,244,240,0.1)' : 'rgba(148,163,184,0.35)';

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 450, easing: 'easeOutQuart' },
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 12, right: 16, bottom: 4, left: 4 } },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          color: isDark ? '#d4d4cf' : '#374151',
          font: { family: "'DM Sans', system-ui, sans-serif", size: 12, weight: '500' },
        },
      },
      tooltip: {
        backgroundColor: isDark ? 'rgba(22,23,20,0.96)' : 'rgba(255,255,255,0.98)',
        titleColor: isDark ? '#f4f4f0' : '#0e0f0c',
        bodyColor: isDark ? '#c8c9c4' : '#454745',
        borderColor: isDark ? 'rgba(244,244,240,0.12)' : 'rgba(14,15,12,0.1)',
        borderWidth: 1,
        cornerRadius: 10,
        padding: 12,
        displayColors: true,
        boxPadding: 6,
        titleFont: { family: "'DM Sans', system-ui, sans-serif", size: 12, weight: '700' },
        bodyFont: { family: "'DM Sans', system-ui, sans-serif", size: 12, weight: '500' },
        callbacks: {
          title: (items) => {
            const i = items?.[0]?.dataIndex;
            return i != null ? labelsAsc[i] : '';
          },
          label: (ctx) => {
            const v = ctx.parsed?.y;
            if (v == null || Number.isNaN(v)) return `${ctx.dataset.label}: —`;
            return `${ctx.dataset.label}:  ${Number(v).toFixed(4)}`;
          },
        },
      },
    },
    scales: {
      x: {
        offset: false,
        border: { display: false },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10,
          color: tickColor,
          font: { family: "'DM Sans', system-ui, sans-serif", size: 11, weight: '500' },
          padding: 10,
        },
        grid: { display: false, drawTicks: false },
      },
      y: {
        border: { display: false },
        title: {
          display: true,
          text: 'Percent',
          color: tickColor,
          font: { family: "'DM Sans', system-ui, sans-serif", size: 12, weight: '600' },
          padding: { bottom: 4 },
        },
        suggestedMin: Number.isFinite(yMin) ? yMin - pad : undefined,
        suggestedMax: Number.isFinite(yMax) ? yMax + pad : undefined,
        ticks: {
          color: tickColor,
          font: { family: "'DM Sans', system-ui, sans-serif", size: 11, weight: '500' },
          padding: 10,
          callback: (v) => Number(v).toFixed(2),
          count: 5,
        },
        grid: {
          display: true,
          drawTicks: false,
          color: gridY,
          lineWidth: 1,
        },
      },
    },
  };

  return { chartData, options };
};

const ChartBody = ({ loading, error, chartBundle, activeLabel }) => {
  if (loading) {
    return (
      <div className="fbil-state">
        <Spin size="large" />
      </div>
    );
  }
  if (error) {
    return <div className="fbil-state fbil-state--error">{error}</div>;
  }
  if (!chartBundle.chartData) {
    return <div className="fbil-state">No series available for this benchmark.</div>;
  }
  return (
    <div className="fbil-chart-card" aria-label={`${activeLabel} trend`}>
      <div className="fbil-chart-wrap">
        <Line data={chartBundle.chartData} options={chartBundle.options} />
      </div>
    </div>
  );
};

/**
 * FBIL benchmarks carousel for the home page.
 */
const FbilBenchmarksPanel = () => {
  const { isDark } = useContext(ThemeContext);
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const cacheRef = useRef(new Map());

  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8083/api/v1/sb';
  const activeMeta = FBIL_BENCHMARKS[activeIndex] || FBIL_BENCHMARKS[0];
  const activeBenchmark = activeMeta.key;

  const fetchBenchmark = useCallback(
    async (benchmarkKey) => {
      const cached = cacheRef.current.get(benchmarkKey);
      if (cached) {
        setProducts(cached);
        setError('');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setProducts([]);
      try {
        const response = await apiClient.get(`${baseUrl}/fbil/benchmark-rates/${benchmarkKey}`);
        const result = response.data;
        const payload = result?.statusCode === 200 ? result.data : result;
        const list = Array.isArray(payload?.benchMarkTrendData) ? payload.benchMarkTrendData : [];
        cacheRef.current.set(benchmarkKey, list);
        setProducts(list);
      } catch (err) {
        console.error('FBIL benchmark fetch failed:', err);
        setError(
          err?.response?.data?.message ||
            'Unable to load FBIL benchmark rates. Migrate data and try again.'
        );
        setProducts([]);
      } finally {
        setLoading(false);
      }
    },
    [baseUrl]
  );

  useEffect(() => {
    fetchBenchmark(activeBenchmark);
  }, [activeBenchmark, fetchBenchmark]);

  const goToIndex = useCallback((nextIndex) => {
    const total = FBIL_BENCHMARKS.length;
    const normalized = ((nextIndex % total) + total) % total;
    setActiveIndex((prev) => {
      if (normalized === prev) return prev;
      setSlideDirection(normalized > prev ? 1 : -1);
      return normalized;
    });
  }, []);

  const goNext = useCallback(() => {
    setSlideDirection(1);
    setActiveIndex((prev) => (prev + 1) % FBIL_BENCHMARKS.length);
  }, []);

  const goPrev = useCallback(() => {
    setSlideDirection(-1);
    setActiveIndex((prev) => (prev - 1 + FBIL_BENCHMARKS.length) % FBIL_BENCHMARKS.length);
  }, []);

  useEffect(() => {
    if (hoverPaused || FBIL_BENCHMARKS.length <= 1) return undefined;
    const timer = window.setInterval(goNext, CAROUSEL_AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [hoverPaused, goNext]);

  const chartBundle = useMemo(
    () => buildChartBundle(products, isDark),
    [products, isDark]
  );

  return (
    <div className={`fbil-page fbil-page--embedded${isDark ? ' fbil-page--dark' : ''}`}>
      <div
        className="fbil-carousel-shell"
        role="region"
        aria-roledescription="carousel"
        aria-label="FBIL benchmark rates"
        onMouseEnter={() => setHoverPaused(true)}
        onMouseLeave={() => setHoverPaused(false)}
      >
        <div className="fbil-carousel-head">
          <div className="fbil-carousel-title-row">
            <p className="fbil-carousel-meta">
              <strong>{activeMeta.label}</strong>
              <span>{` · ${activeMeta.hint}`}</span>
            </p>
            <div className="fbil-carousel-navs">
              <button
                type="button"
                className="fbil-carousel-nav fbil-carousel-nav--prev"
                aria-label="Previous FBIL benchmark"
                onClick={goPrev}
              >
                <LeftOutlined />
              </button>
              <button
                type="button"
                className="fbil-carousel-nav fbil-carousel-nav--next"
                aria-label="Next FBIL benchmark"
                onClick={goNext}
              >
                <RightOutlined />
              </button>
            </div>
          </div>
          <span className="fbil-carousel-count">
            {activeIndex + 1} / {FBIL_BENCHMARKS.length}
          </span>
        </div>

        <div className="fbil-carousel-viewport">
          <AnimatePresence mode="wait" custom={slideDirection}>
            <motion.div
              key={activeBenchmark}
              className="fbil-carousel-slide"
              custom={slideDirection}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              <ChartBody
                loading={loading}
                error={error}
                chartBundle={chartBundle}
                activeLabel={activeMeta.label}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="fbil-carousel-dots" role="tablist" aria-label="FBIL benchmarks">
          {FBIL_BENCHMARKS.map((bench, idx) => (
            <button
              key={bench.key}
              type="button"
              role="tab"
              className={`fbil-carousel-dot${idx === activeIndex ? ' is-active' : ''}`}
              aria-label={`Show ${bench.label}`}
              aria-selected={idx === activeIndex}
              title={bench.label}
              onClick={() => goToIndex(idx)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FbilBenchmarksPanel;
